/** Query-string helpers — build params via object spread, not `URLSearchParams.set`. */

export type QsValue = string | number | boolean | null | undefined;

/**
 * Compact record: drop empty / nullish values.
 * Use with spreads: `compact({ ...(q && { q }), ...(status && { status }) })`.
 */
export function compact<T extends Record<string, QsValue>>(
  record: T,
): { [K in keyof T]?: NonNullable<T[K]> } {
  const out: Record<string, QsValue> = {};
  for (const [key, value] of Object.entries(record)) {
    if (value === undefined || value === null || value === "" || value === false) {
      continue;
    }
    out[key] = value;
  }
  return out as { [K in keyof T]?: NonNullable<T[K]> };
}

/** `?a=1&b=2` or `""` if nothing to serialize. */
export function qs(record: Record<string, QsValue>): string {
  const clean = compact(record);
  const entries = Object.entries(clean).map(([k, v]) => [k, String(v)] as [string, string]);
  if (entries.length === 0) return "";
  return `?${new URLSearchParams(entries).toString()}`;
}
